import { useState, useEffect, useRef } from "react";
import { Container, Row, Col, Button, Form } from "react-bootstrap";
import useApi from '../utilities/useApi';
import { useParams } from "react-router-dom";
import arrayUtilities from "../utilities/arrayUtilities";
import Loading from "./common/loading";
import stringUtilities from "../utilities/stringUtilities";
import { DndContext } from "@dnd-kit/core";
import { DraggableItem } from "./common/draggableItem";
import { DroppableItem } from "./common/droppableItem";
import { ImportPreview } from "./importPreview";
import SuccessModal from "./common/successModal";
import ErrorModal from "./common/errorModal";
import cloneDeep from "lodash.clonedeep";

export default function ImportData({ user }) {

    const inputRef = useRef();
    const { api } = useApi()
    const { organisationId, projectId } = useParams()
    const [isImporting, setIsImporting] = useState(false)
    const [isLoadingSourceColumn, setIsLoadingSourceColumn] = useState(false)
    const [isLoadingTargetColumn, setIsLoadingTargetColumn] = useState(false)
    const [sourceColumnError, setSourceColumnError] = useState(null)
    const [targetColumnError, setTargetColumnError] = useState(null)
    const [file, setFile] = useState(null);
    const [filename, setFilename] = useState("")
    const [canSave, setCanSave] = useState(false)
    const [selectedWorkflow, setSelectedWorkflow] = useState("")
    const [workflows, setWorkflows] = useState(null)
    const [jobs, setJobs] = useState(null)
    const [selectedJob, setSelectedJob] = useState("")
    const [types, setTypes] = useState(null)
    const [selectedType, setSelectedType] = useState("");
    const [sourceProperties, setSourceProperties] = useState(null)
    const [targetPropertyHeaders, setTargetPropertyHeaders] = useState(null)
    const [mappedProperties, setMappedProperties] = useState(null)
    const [dropContainer, setDropContainer] = useState(null)
    const [showPreview, setShowPreview] = useState(false)
    const [successModal, setSuccessModal] = useState(null)
    const [errorModal, setErrorModal] = useState(null)

    const TargetShapeHeadersEnum = {
        "sensorData": "Sensor Data",
        "gpsData": "GPS Data",
        "formData": "Form Data",
    }

    const getEmptyPoint = (workflowId, type) => {
        if (!stringUtilities.isNullOrEmpty(workflowId) && !stringUtilities.isNullOrEmpty(type)) {
            setIsLoadingTargetColumn(true)
            api.getEmptyPoint(
                organisationId, projectId, workflowId, type,
                data => {
                    const { sensorData, gpsData, formData } = data
                    setTargetPropertyHeaders({ sensorData, gpsData, formData })
                    setIsLoadingTargetColumn(false)
                    setTargetColumnError(null)
                },
                error => {
                    console.log(error)
                    setIsLoadingTargetColumn(false)
                    setTargetColumnError(error.message)
                }
            )
        }
    }

    const fetchWorkflows = () => {
        api.getWorkflows(
            organisationId, projectId,
            data => setWorkflows(data),
            error => console.log(error)
        )
    }

    const fetchJobs = () => {
        api.getJobs(
            organisationId,
            projectId,
            data => setJobs(data),
            error => console.log(error)
        )
    }

    const handleChange = () => {
        if (inputRef.current && inputRef.current.files[0]) {
            const inputFile = inputRef.current.files[0];
            if (inputFile.name.toLowerCase().endsWith(".zip")) {
                setFilename(inputRef.current.files[0].name)
                if (inputFile) {
                    const formData = new FormData();
                    formData.append('File', inputFile);
                    setFile(formData);
                    setSourceColumnError(null);
                    setIsLoadingSourceColumn(true)
                    api.importDataAnalyze(
                        organisationId, formData,
                        data => {
                            setSourceProperties(data)
                            setIsLoadingSourceColumn(false)
                            setSourceColumnError(null)
                        },
                        error => {
                            console.log(error);
                            setIsLoadingSourceColumn(false)
                            setSourceColumnError(error.message)
                        }
                    )
                }
            } else setSourceColumnError("Invalid file extension. File should be of type .zip")
        }
    }

    useEffect(() => {
        if (
            file &&
            !stringUtilities.isNullOrEmpty(selectedWorkflow) &&
            !stringUtilities.isNullOrEmpty(selectedJob) &&
            !stringUtilities.isNullOrEmpty(selectedType)
        ) {

            file.set('WorkflowId', selectedWorkflow);
            file.set('JobId', selectedJob);

            let mapping = cloneDeep(mappedProperties);
            
            if (mapping === null) {
                mapping = {}
            }
            
            mapping[selectedType] = "$.formData.Type";
            
            file.set('Mapping', JSON.stringify(mapping));
            setCanSave(true)
        } else {
            setCanSave(false);
        }

    }, [file, selectedWorkflow, selectedJob, mappedProperties, selectedType])

    useEffect(() => {

        let types = [];

        if (!stringUtilities.isNullOrEmpty(selectedWorkflow))
        {
            let workflow = workflows.find(x => x.id === selectedWorkflow);
            if (workflow !== null) {
               let configuration = JSON.parse(workflow.configuration);
               
                if (!arrayUtilities.isNullOrEmpty(configuration.types)) {
                    types = types.concat(configuration.types.map(x => x.name));
                }

                if (!arrayUtilities.isNullOrEmpty(configuration.types2)) {
                    types = types.concat(configuration.types2.map(x => x.name));
                }

                types = arrayUtilities.distinct(types);
                types.sort();               
            }
        }

        setTypes(types);
        setMappedProperties(null);

    }, [selectedWorkflow])

    useEffect(() => {
        getEmptyPoint(selectedWorkflow, selectedType);
    }, [selectedWorkflow, selectedType])

    useEffect(() => {
        fetchWorkflows()
        fetchJobs()
    }, [])

    const handleDragEnd = (event) => {
        if (event.over && event.over.id === dropContainer) {
            setMappedProperties(prevState => ({
                ...prevState,
                [`${ event.active.id }`]: `$.${ event.over.data.current.dataShapeHeader }.${ event.over.id }`
            }))
        }
    }

    const handleTextInput = (key, parent, value) => {
        setMappedProperties(prevState => ({
            ...prevState,
            [`${ value }`]: `$.${ parent }.${ key }`
        }))
    }

    const getPropertyNameByValue = (value) => Object.keys(mappedProperties).find(prop => mappedProperties[prop] === value)

    const removeMappingItem = (property) => {
        const { [property]: removedValue, ...prevState } = mappedProperties
        setMappedProperties(prevState)
    }

    const importData = () => {
        setShowPreview(false)
        setIsImporting(true)
        api.importData(
            organisationId, file,
            () => {
                setIsImporting(false)
                setSuccessModal({
                    message: "Data successfully imported"
                })

                setFile(null)
                setFilename(null)
                setSelectedWorkflow(null)
                setSelectedJob(null)
                setMappedProperties(null)
                setSourceProperties(null)
                setTargetPropertyHeaders(null)
                setDropContainer(null)
            },
            error => {
                console.log(error)
                setIsImporting(false)
                setErrorModal({
                    message: error.message
                })
            }
        )
    }

    return (
        <Container className='pt-5 pb-lg-4 mt-5 mb-sm-2'>
            <Row>
                <Col className='mb-5'>
                    <div className='d-flex justify-content-between my-5 w-100'>
                        <h1 className="h2 m-0">Import Data</h1>
                    </div>
                    {
                        isImporting
                            ? <Loading />
                            : <>
                                <div>
                                    <div>
                                        <Form.Group className='mb-3 row'>
                                            <Form.Label className='form-label fs-sm col-form-label col-md-3' htmlFor="import-data-fileUpload">
                                                File
                                            </Form.Label>
                                            <div className="col-md-6 d-flex align-items-center file-input">
                                                <input className="file-upload-button" ref={ inputRef } onChange={ () => handleChange() } type="file" id="import-data-fileUpload" name="import-data-file" accept='.zip' />
                                                <div className="d-flex flex-column align-items-start">
                                                    <div className="d-flex align-items-center">
                                                        <label htmlFor="import-data-fileUpload"><i className='fi-cloud-upload me-2'></i>Upload File</label>
                                                        <p className="m-0">{ filename }</p>
                                                    </div>
                                                    { !stringUtilities.isNullOrEmpty(sourceColumnError) && <small className='m-0 text-danger'>{sourceColumnError}</small> }
                                                </div>
                                            </div>
                                        </Form.Group>

                                        <Form.Group className='mb-3 row'>
                                            <Form.Label className='form-label fs-sm col-md-3'>
                                                Workflow
                                            </Form.Label>
                                            <div className="d-flex flex-column align-items-start col-md-9">
                                                <Form.Select id="workflow" size='sm' value={ selectedWorkflow } onChange={ e => setSelectedWorkflow(e.target.value) }>
                                                    <option value="">Select a Workflow</option>
                                                    {
                                                        !arrayUtilities.isNullOrEmpty(workflows) && workflows
                                                            .map(workflow => <option key={ workflow.id } value={ workflow.id }>{ workflow.name }</option>)
                                                    }
                                                </Form.Select>
                                                { !stringUtilities.isNullOrEmpty(targetColumnError) && <small className='m-0 text-danger'>{targetColumnError}</small> }
                                            </div>
                                        </Form.Group>
                                        <Form.Group className='mb-3 row'>
                                            <Form.Label className='form-label fs-sm col-md-3'>
                                                Job
                                            </Form.Label>
                                            <div className="d-flex align-items-center col-md-9">
                                                <Form.Select id="jobs" size='sm' value={ selectedJob } onChange={ e => setSelectedJob(e.target.value) }
                                                >
                                                    <option value="">Select a Job</option>
                                                    {
                                                        !arrayUtilities.isNullOrEmpty(jobs) && jobs
                                                            .map(job => <option key={ job.id } value={ job.id }>{ job.name }</option>)
                                                    }
                                                </Form.Select>
                                            </div>
                                        </Form.Group>
                                        <Form.Group className='mb-3 row'>
                                            <Form.Label className='form-label fs-sm col-md-3'>
                                                Type
                                            </Form.Label>
                                            <div className="d-flex align-items-center col-md-9">
                                                <Form.Select size='sm' value={ selectedType } onChange={ e => setSelectedType(e.target.value) }
                                                >
                                                    <option value="">Select a Type</option>
                                                    {
                                                        !arrayUtilities.isNullOrEmpty(types) && types
                                                            .map((type, index) => <option key={`${index}_${type}`} value={ type }>{ type }</option>)
                                                    }
                                                </Form.Select>
                                            </div>
                                        </Form.Group>
                                    </div>
                                    <Form.Group className='mb-3 row'>
                                        <Form.Label className='form-label fs-sm col-md-3'>Data Mapping</Form.Label>
                                        <div className="col-md-9">
                                            <DndContext onDragOver={ event => {
                                                if (event.over) {
                                                    setDropContainer(event.over.id)
                                                }
                                            } } onDragEnd={ event => handleDragEnd(event) }>
                                                <div className="rounded border mt-2 fs-sm">
                                                    <div className="row m-0">
                                                        {
                                                            (isLoadingSourceColumn || isLoadingTargetColumn)
                                                                ? <Loading />
                                                                : <div id="data-mapping-min-height" className="row">
                                                                    <div className="col-4 p-0">
                                                                        { !arrayUtilities.isNullOrEmpty(sourceProperties) && sourceProperties.map(item => <DraggableItem key={ item } item={ item } />) }
                                                                    </div>
                                                                    <div className="col-8 p-0 border-start">
                                                                        <div className="p-2">
                                                                            {
                                                                                targetPropertyHeaders &&
                                                                                Object.entries(targetPropertyHeaders)
                                                                                    .map(([key, value]) =>
                                                                                        <div key={ key } className="mb-4">
                                                                                            <p className="fw-bold m-0">{ TargetShapeHeadersEnum[key] }</p>
                                                                                            <div>
                                                                                                {
                                                                                                    Object.entries(value)
                                                                                                        .map(([k, v]) =>
                                                                                                            <div key={ k } className="row px-4 py-2 d-flex align-items-center">
                                                                                                                <p className="text-truncate text-decoration-none cursor-pointer col-6 m-0 my-2">{ k }</p>
                                                                                                                <div className="col-6">
                                                                                                                    {
                                                                                                                        mappedProperties && Object.values(mappedProperties).includes(`$.${ key }.${ k }`)
                                                                                                                            ? <div id="drop-container" className="d-flex justify-content-between align-items-center px-2 rounded border border-2 border-dark-green bg-light-green text-dark-green">
                                                                                                                                <p className="w-100 m-0 text-truncate">{ getPropertyNameByValue(`$.${ key }.${ k }`) }</p>
                                                                                                                                <button onClick={ () => removeMappingItem(getPropertyNameByValue(`$.${ key }.${ k }`)) } className="btn p-0"><i className="m-0 fs--2 fi-x text-dark-green"></i></button>
                                                                                                                            </div>
                                                                                                                            : <DroppableItem handleChange={ value => handleTextInput(k, key, value) } parent={ key } dndId={ k } />
                                                                                                                    }
                                                                                                                </div>
                                                                                                            </div>
                                                                                                        )
                                                                                                }
                                                                                            </div>
                                                                                        </div>
                                                                                    )
                                                                            }
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                        }
                                                    </div>
                                                </div>
                                            </DndContext>
                                        </div>
                                    </Form.Group>
                                </div>
                                <Form.Group className='mt-4 row justify-content-end'>
                                    <div className="col-md-9 d-flex justify-content-end">
                                        <Button disabled={ !canSave } onClick={ () => setShowPreview(true) } size='sm' className='ms-2'>
                                            Preview
                                        </Button>
                                    </div>
                                </Form.Group>
                            </>
                    }
                </Col>
            </Row>
            {
                showPreview && !arrayUtilities.isNullOrEmpty(workflows) &&
                <ImportPreview
                    organisationId={ organisationId }
                    projectId={ projectId }
                    user={ user }
                    workflows={ workflows }
                    handleClose={ () => setShowPreview(false) }
                    handleImport={ () => importData() }
                    file={file}
                />
            }
            { errorModal && errorModal !== null && <ErrorModal error={errorModal} onDismiss={() => setErrorModal(null)} /> }
            { successModal && successModal !== null && <SuccessModal success={successModal} onDismiss={() => setSuccessModal(null)} /> }
        </Container>
    )
}